/*
 * DevExtensionLive
 * --------
 * Bridge between the VS Code extension and the web app for live editing.
 *
 * Responsibilities (only when running embedded under the extension panel):
 *   1. Handshake with the host (SEEDBOT_LIVE_HELLO from the panel script).
 *   2. Forward inbound SEEDBOT_LIVE_FILE_UPDATE messages into in-app events
 *      so existing components (HtmlView, Studio, ...) react with a tiny
 *      listener each, instead of every component knowing about live mode.
 *   3. Monkey-patch window.fetch so any mutation against /v1/collections/*
 *      or /v1/flows/* bubbles up to the extension as SEEDBOT_LIVE_UI_MUTATION
 *      after the request completes successfully. The extension then asks the
 *      API to re-render the affected resource and writes it back to disk.
 *
 * The patch self-disables if no hello arrives within HANDSHAKE_TIMEOUT_MS,
 * so a regular user session is unaffected.
 */

import { useEffect } from "react";

const HANDSHAKE_TIMEOUT_MS = 1500;
const LIVE_EVENT = "seedbot:live-update";
const MUTATION_PATH_RE = /\/v1\/(collections|flows)\b/i;
const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let liveModeActive = false;
let fetchPatched = false;
let originalFetch = null;

// ── Inbound: extension → web ─────────────────────────────────────────────────

const handleInboundMessage = (event) => {
  const data = event?.data;
  if (!data || typeof data !== "object") return;

  if (data.type === "SEEDBOT_LIVE_HELLO") {
    setLiveMode(!!data.enabled);
    return;
  }

  if (data.type !== "SEEDBOT_LIVE_FILE_UPDATE") return;
  if (!liveModeActive) return;

  const payload = data.payload || data;
  applyLiveUpdate(payload);
};

const applyLiveUpdate = (payload) => {
  const scope = payload?.scope;
  if (!scope) return;

  // Broadcast for component-side listeners (Studio, Data view, ...).
  window.dispatchEvent(new CustomEvent(LIVE_EVENT, { detail: payload }));
};

// ── Outbound: web → extension ────────────────────────────────────────────────

const reportMutation = (request) => {
  if (!liveModeActive) return;
  if (typeof window === "undefined") return;
  if (window.parent === window) return;

  window.parent.postMessage({
    type: "SEEDBOT_LIVE_UI_MUTATION",
    payload: request,
  }, "*");
};

const patchFetch = () => {
  if (fetchPatched) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;

  originalFetch = window.fetch.bind(window);
  fetchPatched = true;

  window.fetch = async (input, init) => {
    const method = (init?.method || (input && input.method) || "GET").toUpperCase();
    const url = typeof input === "string" ? input : input?.url || "";
    const isTrackedMutation =
      MUTATION_METHODS.has(method) && MUTATION_PATH_RE.test(url);

    const response = await originalFetch(input, init);

    if (isTrackedMutation && response.ok) {
      reportMutation({
        url,
        method,
        scope: scopeFromUrl(url),
        target: targetFromUrl(url),
      });
    }

    return response;
  };
};

const unpatchFetch = () => {
  if (!fetchPatched || !originalFetch) return;
  window.fetch = originalFetch;
  originalFetch = null;
  fetchPatched = false;
};

const scopeFromUrl = (url) => {
  if (/\/v1\/collections\b/i.test(url)) return "collections";
  // /v1/flows/<id>/processes/<process_id> → process
  if (/\/v1\/flows\/[^/]+\/processes\b/i.test(url)) return "process";
  if (/\/v1\/flows\/[^/]+\/views\b/i.test(url)) return "views";
  return "flows";
};

const targetFromUrl = (url) => {
  // Best-effort: last path segment that isn't a number. The extension treats
  // this as a hint; the authoritative target is computed server-side when
  // render_code is called with scope + resource_path.
  try {
    const parsed = new URL(url, "http://placeholder.invalid");
    const parts = parsed.pathname.split("/").filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      if (!/^\d+$/.test(parts[i])) return parts[i];
    }
  } catch {}
  return "";
};

// ── Lifecycle ────────────────────────────────────────────────────────────────

const setLiveMode = (enabled) => {
  if (enabled === liveModeActive) return;
  liveModeActive = enabled;

  if (enabled) {
    patchFetch();
  } else {
    unpatchFetch();
  }

  window.dispatchEvent(new CustomEvent("seedbot:live-mode-changed", {
    detail: { enabled },
  }));
};

const DevExtensionLive = () => {

  useEffect(() => {

    if (typeof window === "undefined") return;
    if (window.parent === window) return; // not embedded — no host to talk to

    window.addEventListener("message", handleInboundMessage);

    // If no HELLO arrives in time, stay dormant; the user is running the web
    // app outside the extension panel and we shouldn't touch fetch.
    const timeout = setTimeout(() => {
      if (!liveModeActive) {
        // Still listening for a later HELLO, but no eager fetch patch.
      }
    }, HANDSHAKE_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("message", handleInboundMessage);
      // Don't unpatch on unmount — App lives for the session lifetime, and
      // toggling the component would otherwise leak the live state.
    };

  }, []);

  return null;

};

export const LIVE_UPDATE_EVENT = LIVE_EVENT;
export default DevExtensionLive;
